import { Link } from 'react-router-dom';
import ArticleLayout from './ArticleLayout';

export default function ArticlePricing() {
  return (
    <ArticleLayout
      title="Mulebuy Spreadsheet"
      subtitle="Pricing Explained"
      heroImage="https://readdy.ai/api/search-image?query=Clean%20flatlay%20of%20calculator%20receipts%20coins%20and%20laptop%20showing%20spreadsheet%20with%20price%20columns%20highlighted%2C%20warm%20wooden%20desk%20surface%2C%20soft%20natural%20light%20from%20side%2C%20financial%20planning%20and%20cost%20analysis%20concept%2C%20transparent%20pricing%20theme%2C%20professional%20yet%20accessible%20aesthetic&width=1800&height=840&seq=article-pricing-v2&orientation=landscape"
      heroImageAlt="Mulebuy Spreadsheet pricing explained"
      breadcrumb="Pricing"
      relatedArticles={[
        { title: 'vs Agents', slug: '/mulebuy-spreadsheet-vs-agents' },
        { title: 'Efficiency Tips', slug: '/how-to-use-mulebuy-spreadsheet-efficiently' },
        { title: 'Best Tools', slug: '/best-mulebuy-spreadsheet-tools' },
      ]}
      ctaTitle="save more"
      ctaSubtitle="Understand the complete cost picture of Mulebuy Spreadsheet and maximize your savings on every cross-border purchase."
      seo={{
        title: 'Mulebuy Spreadsheet Pricing Explained – Cost Breakdown & Savings Guide',
        description: 'Mulebuy Spreadsheet pricing explained: complete cost breakdown of cross-border shopping, annual savings examples, and how the spreadsheet method eliminates agent fees. See how much you can save with transparent sourcing.',
        keywords: 'mulebuy spreadsheet pricing, spreadsheet sourcing costs, cross-border shopping costs, agent fee comparison, product sourcing savings',
        path: '/mulebuy-spreadsheet-pricing',
        tags: ['mulebuy spreadsheet', 'pricing', 'costs', 'savings', 'guide'],
      }}
    >
      <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
        One of the most attractive aspects of the <strong>mulebuy spreadsheet</strong> methodology is its cost structure — or rather, the lack of one. Unlike agent services or sourcing platforms that charge fees, the spreadsheet approach itself is free. However, understanding the full cost picture of cross-border shopping helps you budget accurately and avoid surprises.
      </p>

      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-5">Cost Breakdown of a Typical Order</h2>

      <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-8 overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-700">Cost Component</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-primary-500">Typical Range</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500">Notes</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Product price', 'Varies by item', 'Set by supplier, visible upfront'],
              ['Domestic shipping (to warehouse)', 'Free - $5', 'Often free within the source country'],
              ['International shipping', '$10 - $40 per kg', 'Depends on carrier and destination'],
              ['Platform service fee', 'Usually 0%', 'Most platforms do not charge buyer fees'],
              ['Payment processing', '1-3% if applicable', 'Standard credit card or payment fees'],
              ['Customs duties (if any)', 'Varies by country', 'Check your local import thresholds'],
              ['Spreadsheet tool cost', '$0', 'Free tools available for all platforms'],
            ].map((row, i) => (
              <tr key={i} className="border-b border-background-200/70 last:border-b-0">
                <td className="py-3.5 px-4 text-sm text-foreground-700 font-medium">{row[0]}</td>
                <td className="py-3.5 px-4 text-sm text-primary-600 font-semibold">{row[1]}</td>
                <td className="py-3.5 px-4 text-sm text-foreground-600">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-5">Where the Savings Come From</h2>

      <p className="text-base text-foreground-700 leading-relaxed mb-6">
        The <strong>mulebuy spreadsheet</strong> approach saves money in three primary ways. First, by eliminating agent service fees, which typically range from 5% to 15% of the product cost. Second, by enabling better price comparison — when you can see 10 similar products side by side, choosing the best value becomes obvious. Third, by reducing costly mistakes such as ordering the wrong size or from an unreliable supplier, thanks to better organization and record-keeping.
      </p>

      <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-8 overflow-x-auto">
        <h3 className="text-lg font-bold text-foreground-950 mb-4">Annual Savings Example (Moderate Shopper)</h3>
        <table className="w-full min-w-[550px]">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-700">Scenario</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-primary-500">Annual Spend</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500">Agent Fees Saved</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500">Better Deals</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-primary-500">Total Saved</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Casual (10 orders/year)', '$800', '$80', '$60', '$140'],
              ['Regular (25 orders/year)', '$2,000', '$200', '$150', '$350'],
              ['Frequent (50 orders/year)', '$4,000', '$400', '$300', '$700'],
              ['Business (100+ orders/year)', '$8,000+', '$800+', '$600+', '$1,400+'],
            ].map((row, i) => (
              <tr key={i} className="border-b border-background-200/70 last:border-b-0">
                <td className="py-3.5 px-4 text-sm text-foreground-700 font-medium">{row[0]}</td>
                <td className="py-3.5 px-4 text-sm text-foreground-600">{row[1]}</td>
                <td className="py-3.5 px-4 text-sm text-foreground-600">{row[2]}</td>
                <td className="py-3.5 px-4 text-sm text-foreground-600">{row[3]}</td>
                <td className="py-3.5 px-4 text-sm text-primary-600 font-semibold">{row[4]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-5">Hidden Costs to Watch For</h2>
      <p className="text-base text-foreground-700 leading-relaxed mb-6">
        While the <strong>mulebuy spreadsheet</strong> method itself is free, cross-border shopping does involve real costs that new buyers sometimes overlook. International shipping can vary significantly between carriers. Customs duties depend on your country's import regulations and the declared value of your package. Consolidating multiple items into one shipment often reduces per-item shipping costs, and your spreadsheet is the perfect tool for calculating whether consolidation makes financial sense.
      </p>

      <p className="text-base text-foreground-700 leading-relaxed mb-6">
        <Link to="/mulebuy-spreadsheet-vs-agents" className="text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200 cursor-pointer">
          Compare Mulebuy Spreadsheet against traditional agents
        </Link>
        {' '}to see the full cost advantage.{' '}
        <Link to="/" className="text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200 cursor-pointer">
          Return to homepage
        </Link>.
      </p>

      <div className="mt-12 border-t border-background-200 pt-10">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {[
            { q: 'Is the Mulebuy Spreadsheet method really free?', a: 'Yes, the methodology itself costs nothing. You need a spreadsheet tool, and free options like Google Sheets and LibreOffice Calc are fully capable. The only costs are the products you purchase and their associated shipping fees — exactly what you would pay regardless of your organizational method.' },
            { q: 'How much can I realistically save compared to using an agent?', a: 'For a moderate shopper placing 25 orders per year with an average product cost of $80, avoiding a typical 10% agent fee saves approximately $200 annually. Combined with better price comparison enabled by the spreadsheet format, total annual savings of $300-$400 are common and realistic for regular shoppers.' },
            { q: 'Do I need to pay for premium spreadsheet features?', a: 'No. The free versions of all major spreadsheet applications include everything needed for the Mulebuy Spreadsheet workflow. Premium features like advanced analytics, additional storage, or professional templates are entirely optional and not required for effective product sourcing.' },
          ].map((faq) => (
            <div key={faq.q}>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </ArticleLayout>
  );
}